import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { inventoryAPI } from '../utils/apiClient';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/InventoryPage.css';

const InventoryPage = () => {
  const { user, updateUserBalance } = useAuth();
  const [inventory, setInventory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selling, setSelling] = useState({});
  const [filters, setFilters] = useState({
    category: '',
    rarity: '',
    sold: 'false'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchInventory();
    fetchStats();
  }, [filters, currentPage]);

  const fetchInventory = async () => {
    try {
      const params = {
        page: currentPage,
        limit: 12,
        ...filters
      };
      
      const response = await inventoryAPI.getInventory(params);
      setInventory(response.data.inventory || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      // Fallback for MVP - show some demo inventory
      setInventory([
        { id: 1, name: "Silver Ring", rarity: "Common", value: 5.00, image: "/images/ring.jpg" },
        { id: 2, name: "Gold Necklace", rarity: "Rare", value: 25.00, image: "/images/necklace.jpg" },
        { id: 3, name: "Diamond Earrings", rarity: "Epic", value: 75.00, image: "/images/earrings.jpg" }
      ]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await inventoryAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Fallback for MVP
      setStats({
        totalValue: 105.00,
        totalItems: 3,
        boxesOpened: 2,
        rareItems: 1
      });
    }
  };

  const handleSellItem = async (inventoryItemId) => {
    setSelling({ ...selling, [inventoryItemId]: true });
    
    try {
      const response = await inventoryAPI.sellItem(inventoryItemId);
      
      // Update balance
      if (response.data.newBalance !== undefined) {
        updateUserBalance(response.data.newBalance);
      }
      
      // Refresh inventory and stats
      await fetchInventory();
      await fetchStats();
      
      // Show success message (you could implement a toast system)
      const soldPrice = response.data.soldPrice || 0;
      alert(`Item sold for $${soldPrice.toFixed(2)}!`);
      
    } catch (error) {
      console.error('Error selling item:', error);
      const errorMessage = error.response?.data?.error || 'Failed to sell item. Please try again.';
      alert(errorMessage);
    } finally {
      setSelling(prev => ({ ...prev, [inventoryItemId]: false }));
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters({ ...filters, [filterType]: value });
    setCurrentPage(1);
  };

  const getRarityColor = (rarity) => {
    const colors = {
      common: '#8B9DC3',
      uncommon: '#5CB85C',
      rare: '#5BC0DE',
      epic: '#9B59B6',
      legendary: '#F39C12'
    };
    return colors[rarity] || colors.common;
  };

  if (loading) {
    return <LoadingSpinner message="Loading your inventory..." />;
  }

  return (
    <div className="inventory-page">
      <div className="container">
        <div className="inventory-header">
          <h1>Your Inventory</h1>
          <p>Manage your collection and sell items for credits</p>
        </div>

        {stats && (
          <div className="inventory-stats">
            <div className="stat-card">
              <h3>Total Items</h3>
              <div className="stat-value">{stats.totalItems}</div>
            </div>
            <div className="stat-card">
              <h3>Active Items</h3>
              <div className="stat-value">{stats.unsoldItems}</div>
            </div>
            <div className="stat-card">
              <h3>Items Sold</h3>
              <div className="stat-value">{stats.soldItems}</div>
            </div>
            <div className="stat-card">
              <h3>Total Value</h3>
              <div className="stat-value">${stats.totalValue}</div>
            </div>
          </div>
        )}

        <div className="inventory-filters">
          <div className="filter-group">
            <label>Category:</label>
            <select 
              value={filters.category} 
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">All Categories</option>
              <option value="jewelry">Jewelry</option>
              <option value="shoes">Shoes</option>
              <option value="streetwear">Streetwear</option>
              <option value="electronics">Electronics</option>
              <option value="accessories">Accessories</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Rarity:</label>
            <select 
              value={filters.rarity} 
              onChange={(e) => handleFilterChange('rarity', e.target.value)}
            >
              <option value="">All Rarities</option>
              <option value="common">Common</option>
              <option value="uncommon">Uncommon</option>
              <option value="rare">Rare</option>
              <option value="epic">Epic</option>
              <option value="legendary">Legendary</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Status:</label>
            <select 
              value={filters.sold} 
              onChange={(e) => handleFilterChange('sold', e.target.value)}
            >
              <option value="false">Active Items</option>
              <option value="true">Sold Items</option>
            </select>
          </div>
        </div>

        {inventory.length === 0 ? (
          <div className="empty-inventory">
            <div className="empty-icon">📦</div>
            <h2>Your inventory is empty</h2>
            <p>Open some mystery boxes to start collecting items!</p>
            <a href="/" className="cta-btn">Browse Mystery Boxes</a>
          </div>
        ) : (
          <>
            <div className="inventory-grid">
              {inventory.map((inventoryItem) => {
                // Handle both demo and database response structures
                const item = inventoryItem.Item || inventoryItem;
                const mysteryBox = inventoryItem.MysteryBox || { name: 'Unknown Box', price: 5.00 };
                
                return (
                  <div key={inventoryItem.id} className="inventory-item-card">
                    <div 
                      className="item-image-container"
                      style={{ borderColor: getRarityColor(item?.rarity || 'common') }}
                    >
                      {item?.imageUrl ? (
                        <img 
                          src={item.imageUrl} 
                          alt={item?.name || 'Mystery Item'}
                          className="item-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      
                      <div 
                        className="item-placeholder"
                        style={{ display: item?.imageUrl ? 'none' : 'flex' }}
                      >
                        <span className="item-emoji">💎</span>
                      </div>
                      
                      {inventoryItem.isSold && (
                        <div className="sold-overlay">SOLD</div>
                      )}
                    </div>

                    <div className="item-details">
                      <h3 className="item-name">{item?.name || 'Mystery Item'}</h3>
                      <span 
                        className="item-rarity"
                        style={{ color: getRarityColor(item?.rarity || 'common') }}
                      >
                        {(item?.rarity || 'common').toUpperCase()}
                      </span>
                      
                      <div className="item-value">
                        Value: ${parseFloat(item?.value || 0).toFixed(2)}
                      </div>

                      <div className="item-source">
                        From: {mysteryBox?.name || 'Unknown Box'}
                      </div>

                      <div className="item-date">
                        Obtained: {inventoryItem.obtainedAt ? new Date(inventoryItem.obtainedAt).toLocaleDateString() : new Date(inventoryItem.wonAt || Date.now()).toLocaleDateString()}
                      </div>

                      {inventoryItem.isSold ? (
                        <div className="sold-info">
                          <div>Sold for: ${parseFloat(inventoryItem.soldPrice || 0).toFixed(2)}</div>
                          <div>On: {inventoryItem.soldAt ? new Date(inventoryItem.soldAt).toLocaleDateString() : 'N/A'}</div>
                        </div>
                      ) : (
                        <div className="item-actions">
                          <div className="sell-price">
                            Sell for: ${(parseFloat(mysteryBox?.price || 5.00) * parseFloat(item?.sellBackRate || 0.5)).toFixed(2)}
                          </div>
                          <button
                            onClick={() => handleSellItem(inventoryItem.id)}
                            disabled={selling[inventoryItem.id]}
                            className="sell-btn"
                          >
                            {selling[inventoryItem.id] ? 'Selling...' : 'Sell Item'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                
                <span className="page-info">
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {stats?.rarityDistribution && Object.keys(stats.rarityDistribution).length > 0 && (
          <div className="rarity-breakdown">
            <h2>Collection Breakdown</h2>
            <div className="rarity-stats">
              {Object.entries(stats.rarityDistribution).map(([rarity, count]) => (
                <div key={rarity} className="rarity-stat">
                  <div 
                    className="rarity-color"
                    style={{ backgroundColor: getRarityColor(rarity) }}
                  ></div>
                  <span className="rarity-name">{rarity}</span>
                  <span className="rarity-count">{count} items</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryPage;
